import { motion, type Variants } from "motion/react";
import { ArrowDown } from "lucide-react";
import { ShotFrame } from "./ShotFrame";
import { ConsolePickerPreview } from "./previews";
import { useReducedMotion } from "./motion";
import { ADD_TO_DISCORD_URL } from "./constants";
import { DiscordMark } from "../components/DiscordMark";

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.35 } },
};
const item: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

function smoothScrollTo(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
}

export function Hero() {
  const reduced = useReducedMotion();

  return (
    <header className="relative mx-auto flex max-w-6xl flex-col items-center px-5 pb-20 pt-20 text-center sm:pt-28 lg:pt-32">
      <motion.div
        variants={container}
        initial={reduced ? undefined : "hidden"}
        animate={reduced ? undefined : "show"}
        className="flex w-full flex-col items-center"
      >
        <motion.p variants={item} className="lp-kicker mb-5">
          <span className="lp-kicker-rule" aria-hidden />
          insert coin · no install required
        </motion.p>

        <motion.h1
          variants={item}
          className="lp-pixel mx-auto max-w-[18ch] text-[2rem] leading-[1.12] sm:text-[3rem] lg:text-[3.75rem]"
        >
          <span className="text-text-primary">RETRO GAMES.</span>{" "}
          <span className="lp-neon">RIGHT INSIDE</span>{" "}
          <span className="lp-neon-blurple">DISCORD.</span>
        </motion.h1>

        <motion.p
          variants={item}
          className="mx-auto mt-6 max-w-[52ch] text-balance text-base text-text-secondary sm:text-lg"
          style={{ color: "var(--crt-ink-dim)" }}
        >
          Boot up an NES, SNES, Game Boy or a dozen other classics straight in your
          voice channel. Bring your own ROM, hit play, and pass the controller around
          the call.
        </motion.p>

        <motion.div
          variants={item}
          className="mt-9 flex flex-col items-center gap-5 sm:flex-row sm:gap-7"
        >
          <a
            href={ADD_TO_DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="lp-cta text-base sm:text-lg"
          >
            <DiscordMark size={20} /> Add to Discord
          </a>
          <button
            type="button"
            onClick={() => smoothScrollTo("how-to-play")}
            className="lp-ghost inline-flex cursor-pointer items-center gap-1.5 bg-transparent"
          >
            How it works
            <ArrowDown size={16} strokeWidth={1.75} aria-hidden />
          </button>
        </motion.div>

        {/* "Built for Discord" lockup — official mark (blurple) + our own font,
            kept subtle and muted so the branding reads professional, not cheap. */}
        <motion.p variants={item} className="lp-built-for-discord mt-6">
          <DiscordMark size={16} className="lp-built-for-discord-mark" />
          Built for Discord
        </motion.p>
      </motion.div>

      <motion.div
        className="mt-16 w-full max-w-4xl"
        initial={reduced ? undefined : { opacity: 0, y: 40, scale: 0.97 }}
        animate={reduced ? undefined : { opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <ShotFrame
          src="/landing/hero.png"
          label="Console grid — in the Discord activity panel"
          fallback={<ConsolePickerPreview />}
        />
      </motion.div>
    </header>
  );
}
